import React, { useEffect, useState } from 'react'
import injectStyles from 'react-jss'

import { SectionTitle } from 'Components/Section/SectionTitle/index'
import { SectionWrapper } from 'Components/Section/SectionWrapper/index'
import LoadingIndicator from 'Components/LoadingIndicator/LoadingIndicator'
import { authorization } from 'Module/Auth/Auth'
import { getSftpAdminFiles, checkSftpDownloadPermission } from './api'
import { styles } from './styles'
import { triggerDownload, isSensitiveFile } from './utils'
import SftpFilesTable from './SftpFilesTable'
import SensitiveDataModal from './SensitiveDataModal'

const SftpFilesBlock = ({ classes, clientId, countryCode }) => {
  const [files, setFiles] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [sortField, setSortField] = useState('fileName')
  const [sortDirection, setSortDirection] = useState('asc')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [selected, setSelected] = useState(new Set())
  // tokenMap[pageIndex] = continuationToken needed to fetch that page
  // page 0 needs no token (undefined)
  const [tokenMap, setTokenMap] = useState({ 0: undefined })
  const [hasNextPage, setHasNextPage] = useState(false)
  const [pendingDownload, setPendingDownload] = useState(null)
  const [canDownload, setCanDownload] = useState(false)

  const currentUser = authorization.getUser()
  const currentEmail = currentUser
    ? (currentUser.email || '').toLowerCase()
    : ''

  useEffect(() => {
    if (!currentEmail || !countryCode) {
      if (!countryCode) {
        console.warn(
          'SftpFilesBlock: countryCode is undefined, skipping permission check',
        )
      }
      return
    }
    let cancelled = false
    checkSftpDownloadPermission(currentEmail, countryCode)
      .then(result => {
        if (!cancelled) setCanDownload(result)
      })
      .catch(() => {
        if (!cancelled) setCanDownload(false)
      })
    return () => {
      cancelled = true
    }
  }, [currentEmail, countryCode])

  useEffect(() => {
    const fetchFiles = async () => {
      setLoading(true)
      setError(null)
      try {
        const data = await getSftpAdminFiles(clientId, {
          limit: rowsPerPage,
          continuationToken: tokenMap[page],
        })
        setFiles(data.files || [])
        const nextToken = data.nextToken || null
        setHasNextPage(!!nextToken)
        if (nextToken) {
          setTokenMap(prev => ({ ...prev, [page + 1]: nextToken }))
        }
      } catch (err) {
        setError('Failed to load uploaded files.')
      } finally {
        setLoading(false)
      }
      setSelected(new Set())
    }
    fetchFiles()
  }, [clientId, page, rowsPerPage]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSort = field => {
    if (field === sortField) {
      setSortDirection(d => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const sortedFiles = [...files].sort((a, b) => {
    let aVal = a[sortField]
    let bVal = b[sortField]
    if (sortField === 'size') {
      aVal = Number(aVal)
      bVal = Number(bVal)
    } else {
      aVal = String(aVal || '').toLowerCase()
      bVal = String(bVal || '').toLowerCase()
    }
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
    return 0
  })

  const isAllPageSelected =
    sortedFiles.length > 0 && sortedFiles.every(f => selected.has(f.key))

  const handleSelectAll = e => {
    if (e.target.checked) {
      setSelected(prev => {
        const next = new Set(prev)
        sortedFiles.forEach(f => next.add(f.key))
        return next
      })
    } else {
      setSelected(prev => {
        const next = new Set(prev)
        sortedFiles.forEach(f => next.delete(f.key))
        return next
      })
    }
  }

  const handleSelectRow = key => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  const executeDownload = filesToDownload => {
    filesToDownload.forEach(f => triggerDownload(f.downloadUrl, f.fileName))
  }

  const handleDownloadFile = file => {
    if (isSensitiveFile(file.fileName)) {
      setPendingDownload({ files: [file], isBulk: false })
    } else {
      triggerDownload(file.downloadUrl, file.fileName)
    }
  }

  const handleBulkDownload = () => {
    const selected_ = files.filter(f => selected.has(f.key))
    if (selected_.some(f => isSensitiveFile(f.fileName))) {
      setPendingDownload({ files: selected_, isBulk: selected_.length > 1 })
    } else {
      executeDownload(selected_)
    }
  }

  const handleModalConfirm = () => {
    executeDownload(pendingDownload.files)
    setPendingDownload(null)
  }

  const handleModalClose = () => setPendingDownload(null)

  const handleChangePage = (_, newPage) => setPage(newPage)

  const handleChangeRowsPerPage = e => {
    setRowsPerPage(parseInt(e.target.value, 10))
    setPage(0)
    setTokenMap({ 0: undefined })
    setHasNextPage(false)
  }

  const paginationCount = (page + (hasNextPage ? 2 : 1)) * rowsPerPage

  return (
    <SectionWrapper className="section-wrapper">
      <SectionTitle>Uploaded files</SectionTitle>
      <p className={classes.subtitle}>
        Files uploaded by the client through the website under Settings &rarr;
        Secure File Transfer
      </p>

      {loading ? (
        <LoadingIndicator />
      ) : error ? (
        <div>{error}</div>
      ) : (
        <SftpFilesTable
          files={sortedFiles}
          canDownload={canDownload}
          selected={selected}
          isAllPageSelected={isAllPageSelected}
          onSelectAll={handleSelectAll}
          onSelectRow={handleSelectRow}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
          page={page}
          rowsPerPage={rowsPerPage}
          paginationCount={paginationCount}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          onBulkDownload={handleBulkDownload}
          onDownloadFile={handleDownloadFile}
          classes={classes}
        />
      )}
      <SensitiveDataModal
        open={!!pendingDownload}
        onClose={handleModalClose}
        onConfirm={handleModalConfirm}
        isBulk={pendingDownload?.isBulk}
      />
    </SectionWrapper>
  )
}

export default injectStyles(styles)(SftpFilesBlock)
